import { ExpandButton } from '@ui/ElementList/Buttons/ExpandButton';
import {
  CONTENT_CELL_WIDTH,
  LIST_ITEM_HEIGHT,
  LIST_SPACING,
  ROOT_CELL_WIDTH,
  ROOT_CELL_WIDTH_RESPONSIVE,
  Row,
  RowCell,
  useListHover,
  useCustomListRowStyles,
  useListRowStyles,
} from '@ui/ElementList';
import { IconButton } from '@ui/buttons';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import { useToggleElementExpanded } from '@client/hooks/use-expand-elements.hooks';
import { useTriggerContextMenu } from '@client/hooks/use-menu.hooks';
import {
  useFilterResultsBySelectedLifecycles,
  useGetResultsPerGFA,
} from '@features/results/use-results.hooks';
import {
  useNavigateToElementListSelection,
  useProjectNavigation,
} from '@client/hooks/use-router.hooks';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { useIsSelected } from '@client/store/ui/ui.hook';
import { Page } from '@client/store/ui/ui.model';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import { Box, Tooltip } from '@mui/material';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import {
  hasChildren,
  isProductListCategoryGroup,
  isProductListGroup,
  isProductListItem,
} from '@shared/helpers/recursive-element.helpers';
import { isSelectableQuantityUnit } from '@shared/helpers/unit.helpers';
import { OneOfProductListChildren } from '@shared/models/project.model';
import React, { memo, useCallback, useMemo } from 'react';
import ProductListItemBarCharts from './components/ProductListItemBarCharts';
import { ProductListItemName } from './components/ProductListItemName';
import { CompactValueDisplay } from '@features/projects/lists/CompactValueDisplay';
import { Icon } from '@ui/Icon';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import amplitudeLog from '@client/amplitude';
import {
  AMP_PRODUCT_LIST_GO_TO_ELEMENT,
  AMP_PRODUCT_LIST_SELECT_ITEM,
} from '@shared/constants';

interface ProductListItemProps {
  item: OneOfProductListChildren;
  indentation?: number;
}

const ProductListItem: React.FC<ProductListItemProps> = ({
  item,
  indentation = 0,
}) => {
  const { classes } = useCustomListRowStyles();
  const { classes: listClasses } = useListRowStyles();

  const navigateToListSelection = useNavigateToElementListSelection();
  const navigateTo = useProjectNavigation();
  const toggleElementExpanded = useToggleElementExpanded(item);
  const triggerContextMenu = useTriggerContextMenu(item.id);
  const getResultsPerGFA = useGetResultsPerGFA();

  const selected = useIsSelected(item);
  const isExpandAllowed = hasChildren(item);
  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();
  const { someRowIsHovered, setSomeRowIsHovered } = useListHover();

  const lifecycleFilter = useFilterResultsBySelectedLifecycles();
  const results = useMemo(
    () => lifecycleFilter(item.results ?? {}),
    [item.results, lifecycleFilter],
  );

  const resultsPerGFA = getResultsPerGFA(results);
  const co2ePerGFA = getConversionFactorValue(resultsPerGFA, 'co2e');
  const sekPerGFA = getConversionFactorValue(resultsPerGFA, 'sek');

  const [hover, startHover, endHover] = useBooleanState();

  const handleRowClick = useMouseEventCallback(() => {
    if (
      isProductListCategoryGroup(item) ||
      isProductListGroup(item) ||
      isProductListItem(item)
    ) {
      amplitudeLog(AMP_PRODUCT_LIST_SELECT_ITEM, { ItemId: item.id });
      navigateToListSelection(item.id, { page: Page.Products });
    }
  });

  const parentElement = isProductListItem(item) ? item.path.at(-1) : undefined;

  const handleProductToElementListClick = useCallback(
    (e: React.MouseEvent<Element>) => {
      if (!parentElement) return;
      e.stopPropagation();
      amplitudeLog(AMP_PRODUCT_LIST_GO_TO_ELEMENT, {
        ElementId: parentElement.id,
      });
      navigateTo(
        { page: Page.Elements, elementId: parentElement.id },
        { searchParams: { search: null } },
      );
    },
    [parentElement, navigateTo],
  );

  const handleMetricsAreaMouseEnter = useCallback(() => {
    setSomeRowIsHovered(true);
  }, [setSomeRowIsHovered]);

  const handleMetricsAreaMouseLeave = useCallback(() => {
    setSomeRowIsHovered(false);
  }, [setSomeRowIsHovered]);

  const showMetrics = selected || hover || someRowIsHovered;
  const showMetricIcons = selected || hover;

  return (
    <Box
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      onClick={handleRowClick}
      onDoubleClick={toggleElementExpanded}
      className={showMetrics ? classes.showMetrics : classes.hideMetrics}
      sx={{
        width: '100%',
      }}
    >
      <Row
        padding
        height={LIST_ITEM_HEIGHT}
        spacing={LIST_SPACING.DEFAULT}
        hover={hover}
        selected={selected}
        classes={listClasses}
        onContextMenu={triggerContextMenu}
      >
        <RowCell width={ROOT_CELL_WIDTH.CONTENT} indentation={indentation}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto 1fr auto',
              gap: 1,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <ExpandButton
              element={item}
              disabled={!isExpandAllowed}
              hidden={!selected && !hover}
            />
            {isProductListCategoryGroup(item) ? (
              <Icon
                category="elementCategory"
                icon={getElementIconCategoryName(item.id)}
                outerSize="large"
                color="text.secondary"
                size="medium"
              />
            ) : (
              <span />
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                alignItems: 'center',
                gap: 1,
                width: '100%',
              }}
            >
              <ProductListItemName element={item} />
              {parentElement && (
                <Tooltip title="Go to element">
                  <span>
                    <IconButton
                      category="mui"
                      icon="ArrowForward"
                      onClick={handleProductToElementListClick}
                      hidden={!hover}
                    />
                  </span>
                </Tooltip>
              )}
            </Box>
            <ExpressionTypography
              value={getConversionFactorValue(results, item.unit)}
              variant="body2"
              unit={isSelectableQuantityUnit(item.unit) ? item.unit : undefined}
            />
          </Box>
        </RowCell>

        <RowCell
          responsiveWidth={1}
          onMouseEnter={handleMetricsAreaMouseEnter}
          onMouseLeave={handleMetricsAreaMouseLeave}
        >
          <RowCell
            width={ROOT_CELL_WIDTH.BAR}
            responsiveWidth={ROOT_CELL_WIDTH_RESPONSIVE.BAR}
          >
            <ProductListItemBarCharts results={item.results} unit={item.unit} />
          </RowCell>
          <RowCell
            id="metrics-cell"
            align="right"
            width={CONTENT_CELL_WIDTH.METRICS + LIST_SPACING.DEFAULT * 4}
          >
            {co2eVisible.isEnabled && (
              <RowCell
                align="center"
                width={CONTENT_CELL_WIDTH.ICON_METRIC}
                fontWeight={600}
              >
                <CompactValueDisplay
                  value={co2ePerGFA}
                  unit="kgCO2e / m²"
                  tooltipTitle="Emissions per GFA"
                  icon={
                    showMetricIcons ? (
                      <Icon category="costEmission" icon="kgCO2e" />
                    ) : undefined
                  }
                />
              </RowCell>
            )}
            {displayCurrency.isEnabled && (
              <RowCell
                align="center"
                width={CONTENT_CELL_WIDTH.ICON_METRIC}
                fontWeight={400}
              >
                <CompactValueDisplay
                  value={
                    getDisplayValueData(
                      { value: sekPerGFA, unit: 'sek' },
                      { outputUnit: displayCurrency.current },
                    ).valueAsNumber
                  }
                  unit={`${displayCurrency.current} / m²`}
                  tooltipTitle="Expenses per GFA"
                  icon={
                    showMetricIcons ? (
                      <Icon
                        category="currency"
                        icon={displayCurrency.current}
                      />
                    ) : undefined
                  }
                />
              </RowCell>
            )}
          </RowCell>
        </RowCell>
      </Row>
    </Box>
  );
};

export default memo(ProductListItem);
